import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * Reusable centered modal.
 * - Renders via React Portal directly into document.body, bypassing any
 *   parent overflow/transform context that would break position:fixed.
 * - Always centered in the VIEWPORT regardless of page scroll position.
 * - Never exceeds viewport (max-height: calc(100vh - 32px))
 * - Header is sticky (title + close button)
 * - Content area scrolls internally when content is large
 * - Footer is sticky (Cancel / Save buttons)
 * - Body scroll is locked while modal is open
 */
export default function Modal({ title, onClose, footer, children }) {
  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return createPortal(
    /* ── Full-screen backdrop + centering wrapper ── */
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        backgroundColor: 'rgba(0, 0, 0, 0.45)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
      }}
    >
      {/* ── Modal box ── */}
      <div
        style={{
          width: '100%',
          maxWidth: '520px',
          maxHeight: 'calc(100vh - 32px)',
          background: 'white',
          borderRadius: '24px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
          animation: 'fadeInUp 0.2s ease-out',
        }}
      >
        {/* ── Sticky header ── */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            background: 'white',
            zIndex: 10,
            flexShrink: 0,
            borderBottom: '1px solid #F5F5F4',
            padding: '20px 24px 18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h2 className="font-heading text-xl font-semibold text-[#292524]">{title}</h2>
          <button
            onClick={onClose}
            style={{ padding: '6px', borderRadius: '10px', lineHeight: 0 }}
            className="text-[#A8A29E] hover:text-[#292524] hover:bg-[#F5F5F4] transition-colors"
          >
            <X style={{ width: 18, height: 18 }} strokeWidth={1.5} />
          </button>
        </div>

        {/* ── Scrollable content ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {children}
        </div>

        {/* ── Sticky footer (optional) ── */}
        {footer && (
          <div
            style={{
              position: 'sticky',
              bottom: 0,
              background: 'white',
              zIndex: 10,
              flexShrink: 0,
              borderTop: '1px solid #F5F5F4',
              padding: '16px 24px',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
